import React from 'react';


const SecurityResumePreview = ({ componentRef, resumeData = {} }) => {
  // Process highlighting markers from AI response

  const {
    profile_image_url,
    full_name, 
    job_title, 
    email, 
    phone, 
    location, 
    linkedin,
    professional_summary,
    professional_skills,
    soft_skills,
    work_experience,
    relevant_experience,
    education,
    projects,
    achievements,
    certifications,
    languages,
    skills = {},
    key_skills = []
  } = resumeData || {};

  const {
    programming_languages = [],
    frameworks = [],
    tools = [],
    ai_ml_technologies = []
  } = skills || {};

  const isUploadedResume = resumeData && Object.keys(resumeData).length > 0 && !resumeData.isTemplate;
  
  // Enhanced logic to detect when to show user data
  const shouldShowUserData = resumeData && (
      !resumeData.isTemplate || 
      resumeData.full_name || 
      resumeData.email || 
      resumeData.phone || 
      resumeData.location ||
      resumeData.websites ||
      resumeData.professional_summary ||
      resumeData.projects ||
      resumeData.education ||
      resumeData.experience ||
      resumeData.skills ||
      resumeData.key_skills ||
      resumeData.achievements ||
      resumeData.certifications
  );

  // Handle both template and uploaded resume data
  const displaySkills = key_skills.length > 0 || 
    (programming_languages.length > 0 || frameworks.length > 0 || tools.length > 0 || ai_ml_technologies.length > 0);

  // Natural flow - no complex overflow logic needed

  // Skill bar component for visual skill levels
  const SkillBar = ({ skill, level = 80 }) => (
    <div style={{ marginBottom: '8px' }}>
      <div style={{ 
        fontSize: '9pt', 
        marginBottom: '3px', 
        color: '#FFFFFF' 
      }}>
        {skill}
      </div>
      <div style={{ 
        width: '100%', 
        height: '4px', 
        backgroundColor: 'rgba(255,255,255,0.3)', 
        borderRadius: '2px',
        overflow: 'hidden'
      }}>
        <div style={{ 
          width: `${level}%`, 
          height: '100%', 
          backgroundColor: '#FFFFFF',
          borderRadius: '2px'
        }}></div>
      </div>
    </div>
  );

  // Soft skill dots component
  const SoftSkillDots = ({ skill, level = 4 }) => (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      marginBottom: '6px'
    }}>
      <span style={{ fontSize: '9pt', color: '#FFFFFF' }}>{skill}</span>
      <div style={{ display: 'flex', gap: '3px' }}>
        {[1, 2, 3, 4, 5].map(dot => (
          <div
            key={dot}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: dot <= level ? '#FFFFFF' : 'rgba(255,255,255,0.3)'
            }}
          ></div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <div id="section-to-print" ref={componentRef} style={{
        fontFamily: "'Arial', 'Helvetica', sans-serif",
        fontSize: '9pt',
        lineHeight: '1.4',
        color: '#333333',
        backgroundColor: 'white',
        width: 'min(100%, var(--a4-width))',
        maxWidth: 'var(--a4-width)',
        minHeight: 'auto', // Remove fixed height - let content determine height
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        margin: '0 auto',
        display: 'flex',
        // Allow natural growth with content
        flexDirection: 'row',
        // Ensure both columns grow to same height
        alignItems: 'stretch'
      }}>
      
      {/* Left Main Content - Grows naturally with content */}
      <div style={{
        width: '65%',
        backgroundColor: '#FFFFFF',
        padding: '40px 35px 40px 40px',
        // Remove all height constraints - let content flow naturally
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'fit-content'
      }}>
        
        {/* Header */}
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ 
            fontSize: '28px', 
            fontWeight: 'bold', 
            margin: '0 0 5px 0', 
            color: '#333333',
            letterSpacing: '1px'
          }}>
            {shouldShowUserData && full_name ? full_name : "Diana Taylor"}
          </h1>
          <p style={{ 
            fontSize: '14px', 
            margin: '0 0 15px 0',
            color: '#666666',
            fontWeight: '500'
          }}>
            {shouldShowUserData && job_title ? job_title : "Hotel Security Manager"}
          </p>
          <div style={{ 
            width: '80px', 
            height: '2px', 
            backgroundColor: '#333333',
            margin: '0'
          }}></div>
        </div>

        {/* Professional Summary */}
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ 
            fontSize: '14px', 
            fontWeight: 'bold', 
            marginBottom: '12px', 
            color: '#333333',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Professional Summary
          </h2>
          <p style={{ 
            fontSize: '9pt', 
            lineHeight: '1.5', 
            margin: 0, 
            textAlign: 'justify',
            color: '#555555'
          }}>
            {(() => {
              const defaultSummaryText = "Experienced Hotel Security Manager with 9 years of proven success in ensuring the safety and security of guests, staff, and property. Skilled in implementing security protocols, conducting risk assessments, and managing security teams. Achieved a 20% reduction in security incidents through the implementation of new security measures. Recognized for excellent crisis management skills and ability to handle high-pressure situations effectively. Adept at fostering positive relationships with law enforcement agencies and emergency responders.";
              const pickDisplay = (value, fallback = '') => {
                const trimmed = typeof value === 'string' ? value.trim() : '';
                if (trimmed) return trimmed;
                return resumeData?.isTemplate !== false ? fallback : '';
              };
              return pickDisplay(professional_summary, defaultSummaryText);
            })()}
          </p>
        </div>

        {/* Work Experience */}
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ 
            fontSize: '14px', 
            fontWeight: 'bold', 
            marginBottom: '15px', 
            color: '#333333',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Work Experience
          </h2>
          {(work_experience || [
            {
              position: "Senior Hotel Security Manager",
              company: "Luxury Resort",
              start_date: "Jan 2015",
              end_date: "Present",
              responsibilities: [
                "Developed and implemented security policies and procedures to ensure the safety of guests and staff",
                "Managed a team of security officers, scheduling shifts and providing training",
                "Conducted regular security assessments and audits to identify and address vulnerabilities",
                "Collaborated with local law enforcement agencies to respond to incidents and maintain a safe environment",
                "Achieved a 15% reduction in security incidents through improved training and communication strategies"
              ]
            },
            {
              position: "Junior Hotel Security Manager",
              company: "City Hotel",
              start_date: "Mar 2010",
              end_date: "Dec 2014",
              responsibilities: [
                "Patrolled hotel premises to identify and address security risks",
                "Responded to guest and staff concerns regarding safety and security issues",
                "Assisted in the development of emergency response plans and evacuation procedures",
                "Performed investigations into security incidents and implemented corrective actions",
                "Received commendation for quick and effective response to a security breach, preventing further escalation"
              ]
            }
          ]).map((exp, index) => (
            <div key={index} style={{ marginBottom: '20px' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'baseline',
                marginBottom: '4px'
              }}>
                <h3 style={{ 
                  fontSize: '11pt', 
                  fontWeight: 'bold', 
                  margin: 0, 
                  color: '#333333'
                }}>
                  {exp.position}
                </h3>
                <span style={{ 
                  fontSize: '9pt', 
                  color: '#666666',
                  fontWeight: '500'
                }}>
                  {exp.start_date} - {exp.end_date}
                </span>
              </div>
              <p style={{ 
                fontSize: '10pt', 
                margin: '0 0 8px 0',
                color: '#666666',
                fontStyle: 'italic'
              }}>
                {exp.company}
              </p>
              <ul style={{ 
                margin: 0, 
                paddingLeft: '20px', 
                listStyle: 'disc',
                listStylePosition: 'outside'
              }}>
                {(exp.responsibilities || []).map((resp, rIndex) => (
                  <li key={rIndex} style={{ 
                    fontSize: '9pt', 
                    marginBottom: '3px',
                    paddingLeft: '0',
                    marginLeft: '0',
                    lineHeight: '1.4',
                    display: 'list-item',
                    listStyleType: 'disc',
                    listStylePosition: 'outside'
                  }}>
                    {resp}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        
        {/* Education */}
        <div>
          <h2 style={{ 
            fontSize: '14px', 
            fontWeight: 'bold', 
            marginBottom: '15px', 
            color: '#333333',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Education
          </h2>
          {(education || [
            {
              degree: "Masters in Hospitality Management",
              institution: "Culinary Institute of America",
              start_date: "Sep 2010",
              end_date: "May 2012",
              description: "Advanced studies in hospitality management focusing on security and risk management in hotels."
            },
            {
              degree: "Bachelors in Criminal Justice",
              institution: "University of California, Los Angeles",
              start_date: "Sep 2005",
              end_date: "May 2009",
              description: "Studied criminal justice with a focus on security procedures and law enforcement practices."
            }
          ]).map((edu, index) => (
            <div key={index} style={{ marginBottom: '15px' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'baseline',
                marginBottom: '3px'
              }}>
                <h3 style={{ 
                  fontSize: '11pt', 
                  fontWeight: 'bold', 
                  margin: 0, 
                  color: '#333333'
                }}>
                  {edu.degree}
                </h3>
                <span style={{ 
                  fontSize: '9pt', 
                  color: '#666666',
                  fontWeight: '500'
                }}>
                  {edu.start_date} - {edu.end_date}
                </span>
              </div>
              <p style={{ 
                fontSize: '10pt', 
                margin: '0 0 4px 0',
                color: '#666666',
                fontStyle: 'italic'
              }}>
                {edu.institution}
              </p>
              {edu.description && (
                <p style={{ 
                  fontSize: '9pt', 
                  margin: 0,
                  color: '#555555',
                  lineHeight: '1.4'
                }}>
                  {edu.description}
                </p>
              )}
            </div>
          ))}
        </div>
        
        {/* Projects - ALWAYS after education */}
        {projects && projects.length > 0 && (
          <div style={{ marginBottom: '30px' }}>
            <h2 style={{ 
              fontSize: '14px', 
              fontWeight: 'bold', 
              marginBottom: '15px', 
              color: '#333333',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Projects
            </h2>
            <div style={{ fontSize: '9pt', lineHeight: '1.4' }}>
              {projects.map((project, index) => {
                return (
                  <div key={index} style={{ marginBottom: '15px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                      <h3 style={{ fontSize: '12pt', fontWeight: 'bold', margin: 0, color: '#333333' }}>
                        {project.title || "Untitled Project"}
                      </h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {project.year && (
                          <span style={{ 
                            fontSize: '9pt', 
                            color: '#666666', 
                            fontStyle: 'italic'
                          }}>
                            {project.year}
                          </span>
                        )}
                        {project.link && (
                          <a href={project.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: '9pt', color: '#007bff', textDecoration: 'none' }}>
                            View Project
                          </a>
                        )}
                      </div>
                    </div>
                  {project.description && (
                      <p style={{ fontSize: '10pt', margin: '0 0 8px 0', lineHeight: '1.4', color: '#555555' }}>
                        {project.description || "No description provided"}
                      </p>
                    )}
                    {project.technologies && (
                        <p style={{ fontSize: '9pt', margin: 0, color: '#666666', fontStyle: 'italic' }}>
                          Technologies: {
                            Array.isArray(project.technologies) 
                              ? project.technologies.map((item, idx) => (
                                  <span key={idx}>{idx > 0 ? ', ' : ''}{item}</span>
                                ))
                              : typeof project.technologies === 'string'
                                ? project.technologies.split(',').map((item, idx) => (
                                    <span key={idx}>{idx > 0 ? ', ' : ''}{item.trim()}</span>
                                  ))
                                : project.technologies
                          }
                        </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Right Sidebar - Fixed width, grows with content */}
      <div style={{
        width: '35%',
        backgroundColor: '#4a5568',
        color: '#FFFFFF',
        padding: '40px 25px',
        position: 'relative',
        // Remove height constraints - let content determine height
        display: 'flex',
        flexDirection: 'column',
        // Ensure sidebar grows with the content area
        minHeight: 'fit-content'
      }}>
        
        {/* Profile Image */}
        <div style={{
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          margin: '0 auto 25px auto',
          border: '3px solid #FFFFFF',
          overflow: 'hidden',
          backgroundColor: '#2d3748'
        }}>
          {profile_image_url ? (
            <img 
              src={profile_image_url} 
              alt="Profile" 
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
            />
          ) : (
            <div className="header-profile-placeholder">
              <img src="./english.jpeg" alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          )}
        </div>

        {/* Contact Details */}
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ 
            fontSize: '12px', 
            fontWeight: 'bold', 
            marginBottom: '12px', 
            textTransform: 'uppercase', 
            letterSpacing: '1px',
            borderBottom: '2px solid #FFFFFF',
            paddingBottom: '5px'
          }}>
            Contact Details
          </h2>
          <div style={{ fontSize: '8pt', lineHeight: '1.6' }}>
            {shouldShowUserData && email && (
              <p style={{ margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>✉</span> {email}
              </p>
            )}
            {shouldShowUserData && phone && (
              <p style={{ margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>📞</span> {phone}
              </p>
            )}
            {shouldShowUserData && location && (
              <p style={{ margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>🏠</span> {location}
              </p>
            )}
            {shouldShowUserData && linkedin && (
              <p style={{ margin: '0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>🔗</span> {linkedin}
              </p>
            )}
          </div>
        </div>
        
        {/* Skills */}
        {((!resumeData.isTemplate && professional_skills && professional_skills.length > 0) || 
          (resumeData.isTemplate && professional_skills && professional_skills.length > 0)) && (
          <div style={{ marginBottom: '30px' }}>
            <h2 style={{ 
              fontSize: '12px', 
              fontWeight: 'bold', 
              marginBottom: '12px', 
              textTransform: 'uppercase', 
              letterSpacing: '1px',
              borderBottom: '2px solid #FFFFFF',
              paddingBottom: '5px'
            }}>
              Skills
            </h2>
            <div>
              {professional_skills.map((skill, index) => (
                <SkillBar key={index} skill={skill} level={75 + (index % 3) * 8} />
              ))}
            </div>
          </div>
        )}

        {/* Soft Skills */}
        {((!resumeData.isTemplate && soft_skills && soft_skills.length > 0) || 
          (resumeData.isTemplate && soft_skills && soft_skills.length > 0)) && (
          <div style={{ marginBottom: '30px' }}>
            <h2 style={{ 
              fontSize: '12px', 
              fontWeight: 'bold', 
              marginBottom: '12px', 
              textTransform: 'uppercase', 
              letterSpacing: '1px',
              borderBottom: '2px solid #FFFFFF',
              paddingBottom: '5px'
            }}>
              Soft Skills
            </h2>
            <div style={{ fontSize: '9pt', lineHeight: '1.4' }}>
              {soft_skills.map((skill, index) => (
                <p key={index} style={{ margin: '0 0 6px 0', paddingLeft: '8px', textIndent: '-8px' }}>
                  • {skill}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Achievements */}
        {achievements && achievements.length > 0 && (
          <div style={{ marginBottom: '30px' }}>
            <h2 style={{ 
              fontSize: '12px', 
              fontWeight: 'bold', 
              marginBottom: '12px', 
              textTransform: 'uppercase', 
              letterSpacing: '1px',
              borderBottom: '2px solid #FFFFFF',
              paddingBottom: '5px'
            }}>
              Achievements
            </h2>
            <div style={{ fontSize: '9pt', lineHeight: '1.4' }}>
              {achievements.map((achievement, index) => (
                <p key={index} style={{ margin: '0 0 6px 0', paddingLeft: '8px', textIndent: '-8px' }}>
                  • {achievement}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Certifications */}
        {certifications && certifications.length > 0 && (
          <div style={{ marginBottom: '30px' }}>
            <h2 style={{ 
              fontSize: '12px', 
              fontWeight: 'bold', 
              marginBottom: '12px', 
              textTransform: 'uppercase', 
              letterSpacing: '1px',
              borderBottom: '2px solid #FFFFFF',
              paddingBottom: '5px'
            }}>
              Certifications
            </h2>
            <div style={{ fontSize: '9pt', lineHeight: '1.4' }}>
              {certifications.map((cert, index) => {
                const { name, provider } = cert;
                
                return (
                  <div key={index} style={{ marginBottom: '8px' }}>
                    <p style={{ margin: '0 0 2px 0', fontWeight: 'bold' }}>
                      {name}
                    </p>
                    {provider && (
                      <p style={{ margin: '0', opacity: '0.8', fontSize: '8pt' }}>
                        {provider}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}



        {/* Languages */}
        {/* Removed - only show if user has languages data */}
      </div>
    </div>

      {/* Removed separate projects page - projects now flow naturally */}
    {false && (
      <div style={{
        fontFamily: "'Arial', 'Helvetica', sans-serif",
        fontSize: '9pt',
        lineHeight: '1.4',
        color: '#333333',
        backgroundColor: 'white',
        width: '210mm',
        minHeight: '297mm',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        margin: '20px auto',
        display: 'flex'
      }}>
        {/* Left Main Content */}
        <div style={{
          width: '65%',
          backgroundColor: '#FFFFFF',
          padding: '40px 35px 40px 40px'
        }}>
          {/* Projects */}
          <div style={{ marginTop: '30px' }}>
            <h2 style={{ 
              fontSize: '14px', 
              fontWeight: 'bold', 
              marginBottom: '15px', 
              color: '#333333',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Projects
            </h2>
            {projects.map((project, index) => (
              <div key={index} style={{ marginBottom: '15px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'baseline',
                  marginBottom: '3px'
                }}>
                  <h3 style={{ 
                    fontSize: '11pt', 
                    fontWeight: 'bold', 
                    margin: 0, 
                    color: '#333333'
                  }}>
                    {project.title}
                  </h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {project.year && (
                      <span style={{ 
                        fontSize: '8pt', 
                        color: '#666666', 
                        fontStyle: 'italic'
                      }}>
                        {project.year}
                      </span>
                    )}
                    {project.link && (
                      <a 
                        href={project.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ 
                          fontSize: '8pt', 
                          color: '#4a5568',
                          textDecoration: 'none'
                        }}
                      >
                        View Project
                      </a>
                    )}
                  </div>
                </div>
                {project.description && (
                  <p style={{ 
                    fontSize: '9pt', 
                    margin: '0 0 4px 0',
                    color: '#555555',
                    lineHeight: '1.4'
                  }}>
                    {project.description}
                  </p>
                )}
                {project.technologies && project.technologies.length > 0 && (
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '4px',
                    marginTop: '4px'
                  }}>
                    {project.technologies.map((tech, techIndex) => (
                      <span key={techIndex} style={{
                        backgroundColor: '#f7fafc',
                        color: '#4a5568',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '7pt',
                        fontWeight: '500'
                      }}>
                        {tech}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default SecurityResumePreview;


         